import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Typography, IconButton, Box, Tooltip } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import TreatmentPreview, { actionTypes } from './../../components/Treatments/Preview/TreatmentPreview';
import PageContent from '../../components/PageStructure/PageContent/PageContent';
import PageHeader from '../../components/PageStructure/PageHeader/PageHeader';
import InnerContent from '../../components/PageStructure/InnerContent/InnerContent';
import Spinner from '../../components/common/Spinner/Spinner';
import globals from '../../globals/communityGlobals';
import { doubleActionPrevent, formatNhs } from '../../utils/helpers';
import {
  ENABLE_MAPPING_OF_TEAMS,
  ENABLE_RECORDING_OF_WEEKLY_REQUESTED_DAYS,
} from '../../settings/constants/appSettings';

import { Treatment as treatmentPropType } from '../../propTypes/entities/treatment';
import timeSlotsPackagePropType from '../../propTypes/entities/timeSlotsPackage';
import patientPropType from '../../propTypes/entities/patient';

const locale = globals.getArea('treatments');

class TreatmentCreate extends React.Component {
  state = { showErrorMessage: false };
  componentDidMount = () => {
    const { match, getReferral, getDefaultTimeSlotsPackage, getSettings } = this.props;
    getReferral(match.params.id);
    getDefaultTimeSlotsPackage();
    getSettings(ENABLE_RECORDING_OF_WEEKLY_REQUESTED_DAYS);
    getSettings(ENABLE_MAPPING_OF_TEAMS);
  };

  handleGoBack = () => {
    this.props.goBack();
  };

  validateTimes = (activitiesIds, timeSlotsIds, isMultiple) => {
    const { activities, timeSlotsPackage } = this.props;
    const selectedActivities = activities.filter(x => activitiesIds.includes(x.id));
    const timeSlotList = Object.values(timeSlotsIds);
    const timeSlotIdList = Object.keys(timeSlotList[0]);
    const activityDuration = selectedActivities.reduce((total, num) => total + parseInt(num.duration, 10), 0);
    const sortedTimeSlots = timeSlotsPackage.timeSlots
      .filter(ts => timeSlotIdList.includes(ts.id.toString()))
      .sort((a, b) => (a.startTime > b.startTime ? 1 : -1));

    if (sortedTimeSlots.length === 0) return true;
    let maxDuration = sortedTimeSlots[0].duration;
    const minDuration = sortedTimeSlots.reduce((a, b) => (a.duration < b.duration ? a : b));

    if (isMultiple) {
      return minDuration.duration >= activityDuration;
    }
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < sortedTimeSlots.length; i++) {
      const shiftedSlots = sortedTimeSlots.slice(i, sortedTimeSlots.length).concat([...sortedTimeSlots].splice(0, i));
      let duration = sortedTimeSlots[i].duration;
      // eslint-disable-next-line no-plusplus
      for (let j = 0; j < shiftedSlots.length - 1; j++) {
        if (shiftedSlots[j].endTime === shiftedSlots[j + 1].startTime) {
          duration += shiftedSlots[j + 1].duration;
        } else break;
      }
      if (duration > maxDuration) {
        maxDuration = duration;
      }
    }
    return maxDuration >= activityDuration;
  };

  handleSave = doubleActionPrevent.bind(this)(
    'Save',
    async (model, isSpecific) => {
      const {
        createTreatment,
        referral: { id: referralId },
        timeSlotsPackage: { id: timeSlotsPackageId },
      } = this.props;

      if (model.isSpecific || this.validateTimes(model.activitiesIds, model.timeSlotsMatrix, model.isMultiple)) {
        const trimmedDateFrom = model.dateFrom
          ? moment(model.dateFrom)
              .startOf('day')
              .format(globals.dateFormats.localApiFormat)
          : undefined;
        const trimmedDateTo = model.dateTo
          ? moment(model.dateTo)
              .startOf('day')
              .format(globals.dateFormats.localApiFormat)
          : undefined;
        const singleOrgUnit = [model.orgUnitIds];
        const singleTeam = model.teamIds;
        await createTreatment({
          treatment: {
            referralId,
            timeSlotsPackageId,
            isSpecific,
            ...model,
            orgUnitIds: singleOrgUnit,
            teamId: singleTeam,
            dateFrom: trimmedDateFrom,
            dateTo: trimmedDateTo,
          },
        });
        this.setState({ showErrorMessage: false });
      } else {
        this.setState({ showErrorMessage: true });
      }
    },
    () => {
      const {
        navigate,
        referral: { id: referralId },
      } = this.props;
      if (!this.state.showErrorMessage) navigate(`/Patients/Patient/Referrals/${referralId}`);
    },
  );

  render() {
    const {
      pageTitle,
      isFetching,
      referral,
      jwtToken,
      timeSlotsPackage: { timeSlots = [], id: timeSlotsPackageId } = {},
      enableRecordingOfWeeklyRequestedDays,
      enableMappingOfTeams,
    } = this.props;
    const { patient } = referral || {};

    return (
      <PageContent pageTitle={pageTitle} permanentSidePanel>
        <div className="flex-page-content content-wrapper">
          <PageHeader>
            <Box className="title-section" display="flex" alignItems="center" px={2} py={1}>
              <Box>
                <Tooltip title={locale.getMsg('back')}>
                  <IconButton
                    className="back-btn"
                    aria-label="back"
                    size="small"
                    color="primary"
                    onClick={this.handleGoBack}
                  >
                    <ArrowBackIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box flexGrow={1}>
                <Typography variant="h3">{locale.getMsg('createPageHeader')}</Typography>
                <Typography variant="h6">
                  {patient &&
                    locale.getMsg('patientInfoHeader', [patient.patientFullName, formatNhs(patient.nhsNumber)])}
                </Typography>
              </Box>
            </Box>
          </PageHeader>
          <InnerContent>
            <div className="full-page">
              <div className="tabs">
                {referral && !isFetching ? (
                  <TreatmentPreview
                    timeSlots={timeSlots}
                    timeSlotsPackageId={timeSlotsPackageId}
                    patientAddress={referral.patient.address}
                    patient={referral.patient}
                    treatmentAddress={null}
                    saveCallback={this.handleSave}
                    actionType={actionTypes.create}
                    showErrorMessage={this.state.showErrorMessage}
                    referralDateReceived={referral.dateReceived}
                    referralServiceEndDate={referral.referralService ? referral.referralService.endDate : undefined}
                    jwtToken={jwtToken}
                    enableRecordingOfWeeklyRequestedDays={enableRecordingOfWeeklyRequestedDays}
                    enableMappingOfTeams={enableMappingOfTeams}
                  />
                ) : (
                  <Spinner />
                )}
              </div>
            </div>
          </InnerContent>
        </div>
      </PageContent>
    );
  }
}

TreatmentCreate.defaultProps = {
  failureMessage: '',
  referral: null,
  timeSlotsPackage: {
    timeSlots: [],
  },
  activities: [{}],
};

TreatmentCreate.propTypes = {
  getDefaultTimeSlotsPackage: PropTypes.func.isRequired,
  pageTitle: PropTypes.string.isRequired,
  goBack: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  createTreatment: PropTypes.func.isRequired,
  activities: PropTypes.arrayOf(PropTypes.object).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      Id: PropTypes.string,
    }),
  }).isRequired,
  getReferral: PropTypes.func.isRequired,
  referral: PropTypes.shape({
    dateReceived: PropTypes.string,
    id: PropTypes.number,
    locationType: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    orgUnits: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    ),
    patient: patientPropType,
    referenceCode: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    referralPriority: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    referralSource: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    treatments: PropTypes.arrayOf(treatmentPropType),
  }),
  isFetching: PropTypes.bool.isRequired,
  timeSlotsPackage: timeSlotsPackagePropType,
};

export default TreatmentCreate;
